import { differenceInCalendarDays, format, isBefore } from "date-fns";
import { useFormik } from "formik";
import * as Yup from "yup";
import { requiredIfPredicate } from "../../../util/validation";
import { AutoCompleteItem } from "../../autocomplete/interface";
import {
  FirmaRelationKode,
  FirmaRelationKodeDict,
} from "../OpretPerson/useOpretPerson";
import { useMutation } from "@tanstack/react-query";
import { patch, post } from "../../../api/util";
import { PersonDto } from "../../../interfaces/schema";
import { getPatchOperations } from "../../../util/form";
import {
  cprLoebeNrSchema,
  stripCprRestrictedChanges,
} from "../../../util/cprAccess";
import { dateIsWithinInterval } from "../../../util/date";
import { logSafeError } from "../../../util/logSafeError";

export function useBeskikketAdvokat(
  person: PersonDto,
  cpr: string,
  canReadCpr: boolean,
) {
  const { personFirma } = person;
  const initialFoedselsDato = person.foedselsDato
    ? new Date(person.foedselsDato)
    : undefined;

  const {
    mutateAsync: patchPersonMutation,
    isPending: isPatchLoading,
    error: patchError,
    data: patchData,
    reset: resetPersonQuery,
  } = useMutation({
    mutationFn: async (body: {
      foedselsDato?: string;
      CprLoebeNr?: string;
    }) => {
      return await patch({
        query: `person/${person.id}`,
        body: JSON.stringify(getPatchOperations(body)),
      });
    },
  });
  const beskikAdvokatQuery = useMutation({
    mutationFn: (dto: BeskikAdvokatDTO) => {
      return post<{ id: string; fornavne: string; efternavn: string }>({
        query: `person/${person.id}/wizards/beskikadvokat`,
        body: dto,
      });
    },
  });
  const beskikkelseValidation = useFormik<BeskikketAdvokatFormik>({
    validateOnMount: true,
    initialValues: {
      foedselsDato: initialFoedselsDato,
      cprNr: cpr ? cpr.split("-")[1] : "",
      firmaRelation: FirmaRelationKodeDict.ansat,
      erUdenlandskAdvokat: false,
      udenlandskAdvokatSamfund: undefined,
      beskikkelseFraDato: undefined,
      aarsag: "",
      emailArbejde: "",
      nytFirma: undefined,
      nytFirmaFraDato: undefined,
      sammeFirma: 1,
      priorieteretPrimaerFra: undefined,
    },
    validationSchema: Yup.object({
      foedselsDato: Yup.date()
        .nullable()
        .required("Fødselsdato skal være sat")
        .test("foedselsDato", "Årstal skal være mindst 1900", (item) => {
          if (!item) return true;
          return item.getFullYear() > 1899;
        })
        .test("foedselsDato", "Må ikke være i fremtiden", (item) => {
          if (!item) return true;
          const date = new Date(item);
          return isBefore(date, new Date());
        }),
      cprNr: cprLoebeNrSchema(canReadCpr),
      udenlandskAdvokatSamfund: Yup.object().test(
        "udenlandskAdvokatSamfund",
        "Udenlandsk advokatsamfund skal være sat når personen er EU-advokat",
        function (udenlandskAdvokatSamfund) {
          const erUdenlandskAdvokat = this.parent.erUdenlandskAdvokat;
          if (erUdenlandskAdvokat) {
            return udenlandskAdvokatSamfund !== undefined;
          }
          return true;
        },
      ),
      beskikkelseFraDato: Yup.date()
        .nullable()
        .required("Startdato skal være sat "),
      aarsag: Yup.string(),
      nytFirma: Yup.object()
        .test(
          "sammefirma",
          "Nyt firma skal være sat, dersom personen ikke fortsætter i samme firma",
          function (nytFirma) {
            return requiredIfPredicate(this.parent.sammeFirma > 1, nytFirma);
          },
        )
        .test(
          "nytFirma",
          "Nyt firma skal være sat, dersom personen ikke har aktiv firmarelation",
          function (nytFirma) {
            const compareDate = this.parent.beskikkelseFraDato
              ? this.parent.beskikkelseFraDato
              : new Date();
            const aktiveFirma = person.personFirma?.filter(
              (element) =>
                differenceInCalendarDays(
                  new Date(element?.periode.tilDatoInklusiv),
                  compareDate,
                ) >= 0,
            );
            return requiredIfPredicate(aktiveFirma?.length === 0, nytFirma);
          },
        ),
      emailArbejde: Yup.string().email("Email har forkert format"),
      nytFirmaFraDato: Yup.date()
        .nullable()
        .test(
          "nytFirmaFraDato",
          "Start dato skal være sat",
          function (emailArbejde) {
            const compareDate = this.parent.beskikkelseFraDato
              ? this.parent.beskikkelseFraDato
              : new Date();
            const aktiveFirma = person.personFirma?.filter(
              (element) =>
                differenceInCalendarDays(
                  new Date(element?.periode.tilDatoInklusiv),
                  compareDate,
                ) >= 0,
            );
            return requiredIfPredicate(
              aktiveFirma?.length === 0 || this.parent.sammeFirma > 1,
              emailArbejde,
            );
          },
        ),
    }),
    onSubmit: async (values) => {
      const {
        aarsag,
        emailArbejde,
        beskikkelseFraDato,
        erUdenlandskAdvokat,
        nytFirma,
        firmaRelation,
        udenlandskAdvokatSamfund,
        cprNr,
        foedselsDato,
        sammeFirma,
        nytFirmaFraDato,
        priorieteretPrimaerFra,
      } = values;
      try {
        if (!!foedselsDato && foedselsDato !== initialFoedselsDato) {
          // ADV-349: uden CprRead sendes CprLoebeNr aldrig, og fødselsdatoen ikke for en
          // person med CPR-nummer — backenden ville afvise PATCH'en.
          const patchBody = stripCprRestrictedChanges(
            {
              foedselsDato: foedselsDato
                ? format(foedselsDato, "yyyy-MM-dd")
                : "",
              CprLoebeNr: cprNr,
            },
            {
              canReadCpr,
              harCprNummer: !!person.harCprNummer,
              foedselsDato: person.foedselsDato,
            },
          );
          if (Object.keys(patchBody).length > 0) {
            await patchPersonMutation(patchBody);
          }
        }
        const aktiveFirmaer =
          personFirma?.filter((pf) =>
            beskikkelseFraDato
              ? dateIsWithinInterval(
                  beskikkelseFraDato,
                  new Date(pf.periode.fraDato),
                  new Date(pf.periode.tilDatoInklusiv),
                )
              : false,
          ) ?? [];
        const futureFirmaer =
          beskikkelseFraDato && person.personFirma
            ? person.personFirma.filter(
                (element) =>
                  new Date(element?.periode.fraDato) > beskikkelseFraDato,
              )
            : [];

        const dto: BeskikAdvokatDTO = {
          aarsag,
          udenlandskAdvokatSamfundId:
            erUdenlandskAdvokat && udenlandskAdvokatSamfund
              ? udenlandskAdvokatSamfund.id
              : undefined,
          fraDato: beskikkelseFraDato
            ? format(beskikkelseFraDato, "yyyy-MM-dd")
            : "",
          nyPersonFirmarelation:
            nytFirma && (aktiveFirmaer?.length === 0 || sammeFirma > 1)
              ? {
                  firmaId: nytFirma.id,
                  personfirmatypeNr: firmaRelation.kode,
                  periodeFraDato: nytFirmaFraDato
                    ? format(nytFirmaFraDato, "yyyy-MM-dd")
                    : "",
                  emailArbejde: emailArbejde ? emailArbejde : undefined,
                  priorieteretPrimaerFra: priorieteretPrimaerFra
                    ? format(priorieteretPrimaerFra, "yyyy-MM-dd")
                    : undefined,
                }
              : undefined,
          straksDeponering: false,
          fortsaettendePersonFirmarelationer:
            sammeFirma < 3
              ? [
                  // eslint-disable-next-line no-unsafe-optional-chaining
                  ...aktiveFirmaer?.map((af) => af.id),
                  ...futureFirmaer.map((ff) => ff.id),
                ]
              : [],
          ophoerendePersonFirmarelationer:
            sammeFirma === 3 ? aktiveFirmaer?.map((af) => af.id) : [],
        };
        await beskikAdvokatQuery.mutateAsync(dto);
      } catch (error) {
        logSafeError(error);
      }
    },
  });

  return {
    formik: beskikkelseValidation,
    beskikAdvokatQuery,
    isPatchLoading,
    patchError,
    patchData,
    resetPersonQuery,
  };
}

export type BeskikketAdvokatFormik = {
  foedselsDato: undefined | Date;
  cprNr: string;
  erUdenlandskAdvokat: boolean;
  udenlandskAdvokatSamfund: IUdenlandskAdvokatsamfund | undefined;
  beskikkelseFraDato: Date | undefined;
  aarsag: string;
  emailArbejde: string;
  sammeFirma: 1 | 2 | 3; // 1 - Ja, 2 - Ja, men også nyt. 3 - Nej (skal vælge ny relation)
  nytFirma: AutoCompleteItem | undefined;
  firmaRelation: FirmaRelationKode;
  nytFirmaFraDato: Date | undefined;
  priorieteretPrimaerFra: Date | undefined;
};

export interface BeskikAdvokatDTO {
  fraDato: string;
  straksDeponering: boolean;
  aarsag: string;
  udenlandskAdvokatSamfundId?: string;
  ophoerendePersonFirmarelationer?: string[];
  fortsaettendePersonFirmarelationer?: string[];
  nyPersonFirmarelation?: {
    personfirmatypeNr: "Ansat" | "Partner" | "Ejer" | "Stifter" | "AnsatOgsaaI";
    periodeFraDato: string;
    tilDatoInklusiv?: string;
    bemaerkninger?: string;
    priorieteretPrimaerFra?: string;
    emailArbejde?: string;
    firmaId: string;
  };
}
